import type { HistoryItem, LightHistoryItem } from '../types';

/**
 * History entries come in two shapes: the current light one, which keeps title, prompt, cost
 * and file references on the entry itself, and the old one, which keeps a node's whole data.
 * An entry can also carry both (a light entry once given an empty `data`), so every field is
 * read from the entry first and from its `data` second.
 */

export type AnyHistoryItem = HistoryItem | LightHistoryItem;

export interface HistoryItemFields {
    title?: string;
    prompt?: string;
    cost?: number;
    imageRef?: string;
    videoRef?: string;
}

const dataOf = (item: AnyHistoryItem): Partial<HistoryItem['data']> =>
    ('data' in item && item.data && typeof item.data === 'object' ? item.data : {});

export const historyItemFields = (item: AnyHistoryItem): HistoryItemFields => {
    const light = item as Partial<LightHistoryItem>;
    const data = dataOf(item);
    return {
        title: light.title ?? data.title,
        prompt: light.prompt ?? data.prompt,
        cost: typeof light.cost === 'number' ? light.cost : typeof data.cost === 'number' ? data.cost : undefined,
        imageRef: light.imageRef ?? data.generatedImageUri ?? data.imageUrl,
        videoRef: light.videoRef ?? data.videoUri,
    };
};

/** What the entry cost, 0 when it does not say. */
export const historyItemCost = (item: AnyHistoryItem): number => historyItemFields(item).cost ?? 0;

/** The total cost of a workspace's History. */
export const historyTotalCost = (history: AnyHistoryItem[]): number =>
    history.reduce((sum, item) => sum + historyItemCost(item), 0);

/** An old entry that holds a node's whole data (as opposed to a light one, or a light one with an empty `data`). */
export const isFullDataHistoryItem = (item: AnyHistoryItem): item is HistoryItem =>
    'data' in item && !!item.data && Object.keys(item.data).length > 0
    && !('imageRef' in item) && !('videoRef' in item) && !('cost' in item) && !('title' in item) && !('prompt' in item);
